import React, { useState, useEffect } from "react";
import { Plus, Minus } from "react-feather";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import MenuHeader from "../../../InitialPage/Sidebar/MenuHeader.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SessionClient from "../../components/SessionControl/sessionclient.jsx";
import { FaTrash } from 'react-icons/fa';

const OrderSummary = () => {
  const MySwal = withReactContent(Swal);
  const [searchParams] = useSearchParams();
  const [recaporder, setRecaporder] = useState([]);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartQuantity, setCartQuantity] = useState(0); // Ajout du state cart quantity
  const [devise, setDevise] = useState(JSON.parse(localStorage.getItem("restaurant"))?.Restaurant?.devise ?? JSON.parse(localStorage.getItem("restaurant"))?.restaurant?.devise)
  const [reference, setReference] = useState(null);
  
  const restau = searchParams.get("r");
  const table = searchParams.get("t");

  // 🔄 Vérifier que le restaurant correspond aux données en session
  useEffect(() => {
    const lastRestaurantId = localStorage.getItem('last_restaurant_id');
    const currentRestaurantId = restau?.toString();
    
    // Si le restaurant a changé depuis la dernière visite
    if (lastRestaurantId && currentRestaurantId && lastRestaurantId !== currentRestaurantId) {
      console.log('⚠️ Restaurant différent détecté dans order-summary');
      // Rediriger vers le menu du nouveau restaurant
      window.location.href = `/homemenu?r=${restau}&t=${table}`;
      return;
    }
  }, [restau, table]);

  useEffect(() => {
    fetchOrderDetails();
  }, []);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const client = JSON.parse(sessionStorage.getItem("client"));
      
      if (!client?.reference) {
        setError("Aucune commande trouvée");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${base_api_url}api/commande/${client.reference}`);
      
      // Handle successful responses (200, 201)
      if (response.status === 200 || response.status === 201) {
        console.log("Order details:", response.data);
        
        setOrderData(response.data);
        setReference(response.data.reference);
        console.log("restau:", localStorage.getItem("restaurant"));
        setDevise(JSON.parse(localStorage.getItem("restaurant"))?.restaurant?.devise ?? JSON.parse(localStorage.getItem("restaurant"))?.restaurant?.devise);
        
        // Transform the API response to match the current structure
        const transformedData = response.data.details?.map((detail) => {
          const article = detail.article?.[0]; // Get first article
          return {
            id_details: detail.id_details,
            id_article: article?.id_article,
            title: article?.nom || "Article inconnu",
            underTitle: article?.description || "Pas de description",
            prix: `${detail.prix_unitaire} ${devise}`,
            quantity: detail.quantite,
            prix_total: detail.prix_total,
            nb_complement: article?.nb_complement,
            commentaire: detail.commentaire,
            image_url: article?.image_url && !article?.image_url.startsWith('data:image') ? `${base_api_url}api/recuperer/${article.image_url}` : article?.image_url
          };
        }) || [];
        
        setRecaporder(transformedData);

        // Calcul de la quantité d'éléments dans le panier
        const totalQuantity = response.data.details?.reduce((total, detail) => {
          return total + (detail.quantite || 0);
        }, 0) || 0;
        
        setCartQuantity(totalQuantity);
        setError(null);
        
        // Show info if no items in order
        if (transformedData.length === 0) {
          MySwal.fire({
            icon: "info",
            // title: "Panier vide",
            text: "Votre panier est vide. Ajoutez des articles pour continuer.",
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "pixel-toast",
            },
          });
        }
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      
//       let errorTitle = "Erreur de chargement";
      let errorMessage = "Erreur lors du chargement de la commande";
      
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        switch (status) {
          case 404:
//             errorTitle = "Commande introuvable";
            errorMessage = "Aucune commande trouvée. Votre session a peut-être expiré.";
            break;
            
          case 401:
//             errorTitle = "Session expirée";
            errorMessage = "Votre session a expiré. Veuillez retourner au menu principal.";
            break;
            
          case 403:
//             errorTitle = "Accès refusé";
            errorMessage = "Vous n'avez pas accès à cette commande.";
            break;
            
          case 500:
//             errorTitle = "Erreur du serveur";
            errorMessage = "Une erreur s'est produite sur le serveur. Veuillez réessayer.";
            break;
            
          case 503:
//             errorTitle = "Service indisponible";
            errorMessage = "Le service est temporairement indisponible.";
            break;
            
          default:
//             errorTitle = `Erreur ${status}`;
            errorMessage = errorData?.message || "Une erreur inattendue s'est produite.";
        }
      } else if (error.request) {
//         errorTitle = "Problème de connexion";
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
      }
      
      setError(errorMessage);
      
      // Show error notification
      MySwal.fire({
        icon: "error",
        // title: errorTitle,
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
      
      // Set empty data on error
      setRecaporder([]);
      setOrderData(null);
      setCartQuantity(0);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (detailId, newQuantity) => {
    if (newQuantity < 1) return;
    
    // Store original data for potential rollback
    const originalRecaporder = [...recaporder];
    const originalCartQuantity = cartQuantity;
    
    try {
      // Calculate the quantity difference for cart update
      const oldItem = recaporder.find(item => item.id_details === detailId);
      const quantityDifference = newQuantity - (oldItem?.quantity || 0);
      
      // Optimistic update - update UI immediately
      const updatedRecaporder = recaporder.map(item => 
        item.id_details === detailId 
          ? { 
              ...item, 
              quantity: newQuantity,
              prix_total: parseFloat(item.prix.replace(/[^\d.-]/g, '')) * newQuantity
            }
          : item
      );
      
      setRecaporder(updatedRecaporder);
      
      // Update cart quantity based on the difference
      const newCartQuantity = cartQuantity + quantityDifference;
      setCartQuantity(newCartQuantity);
      
      // Update localStorage immediately for consistency
      localStorage.setItem('quantite', newCartQuantity);
      const newTotal = updatedRecaporder.reduce((total, item) => {
        const prix = parseFloat(item.prix.replace(/[^\d.-]/g, ''));
        return total + (prix * item.quantity);
      }, 0);
      localStorage.setItem('total', newTotal);

      // Call backend API to update quantity
      const response = await axios.put(`${base_api_url}api/detailscmd/${detailId}/${newQuantity}`);
      
      // Handle successful responses (200, 201)
      if (response.status === 200 || response.status === 201) {
        console.log(`Quantité mise à jour avec succès pour le détail ${detailId}: ${newQuantity}`);
        
        // Update orderData if available to keep it in sync
        if (orderData) {
          const updatedOrderData = {
            ...orderData,
            details: orderData.details?.map(detail => 
              detail.id_details === detailId 
                ? { ...detail, quantite: newQuantity }
                : detail
            )
          };
          setOrderData(updatedOrderData);
        }
        
        // Show success notification
        MySwal.fire({
          icon: "success",
          // title: "Quantité mise à jour !",
          text: `La quantité a été modifiée avec succès.`,
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          },
        });
      }
      
    } catch (error) {
      console.error("Erreur lors de l'ajustement de la quantité:", error);
      
      // Rollback optimistic updates on error
      setRecaporder(originalRecaporder);
      setCartQuantity(originalCartQuantity);
      
      // Restore original localStorage values
      localStorage.setItem('quantite', originalCartQuantity);
      const originalTotal = originalRecaporder.reduce((total, item) => {
        const prix = parseFloat(item.prix.replace(/[^\d.-]/g, ''));
        return total + (prix * item.quantity);
      }, 0);
      localStorage.setItem('total', originalTotal);
      
//       let errorTitle = "Erreur de mise à jour";
      let errorMessage = "Impossible de mettre à jour la quantité. Veuillez réessayer.";
      
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        switch (status) {
          case 400:
//             errorTitle = "Quantité invalide";
            errorMessage = errorData?.message || "La quantité doit être un nombre valide supérieur à 0.";
            break;
            
          case 401:
//             errorTitle = "Session expirée";
            errorMessage = "Votre session a expiré. Veuillez rafraîchir la page.";
            break;
            
          case 404:
//             errorTitle = "Article introuvable";
            errorMessage = "L'article que vous tentez de modifier n'existe plus dans votre commande.";
            // In this case, we might want to remove the item from the UI
            setRecaporder(prev => prev.filter(item => item.id_details !== detailId));
            break;
            
          case 409:
//             errorTitle = "Conflit de modification";
            errorMessage = "Une autre modification est en cours. Veuillez réessayer dans quelques instants.";
            break;
            
          case 422:
//             errorTitle = "Modification impossible";
            errorMessage = errorData?.message || "Cette modification n'est pas autorisée pour cet article.";
            break;
            
          case 500:
//             errorTitle = "Erreur du serveur";
            errorMessage = "Une erreur s'est produite sur le serveur. Veuillez réessayer.";
            break;
            
          case 503:
//             errorTitle = "Service indisponible";
            errorMessage = "Le service est temporairement indisponible. Veuillez réessayer plus tard.";
            break;
            
          default:
//             errorTitle = `Erreur ${status}`;
            errorMessage = errorData?.message || "Une erreur inattendue s'est produite.";
        }
      } else if (error.request) {
//         errorTitle = "Problème de connexion";
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
      }
      
      // Show error notification
      MySwal.fire({
        icon: "error",
        // title: errorTitle,
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    }
  };
  
  const handleDelete = async (id_article) => {
    try {
      const response = await axios.put(
        `${base_api_url}api/rmdetailscmd/${id_article}/${reference}`
      );

      if (response.status === 200) {
        MySwal.fire({
          icon: "success",
          // title: "Panier mis à jour !",
          text: "Article supprimé avec succès !",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          },
        });

        // 🔁 Recharge le panier depuis l’API
        fetchOrderDetails();
      } else {
        console.error("Erreur inattendue lors de la suppression :", response);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      alert("Impossible de supprimer cet article.");
    }
  };


  
  const handleIncrement = async (detailId, currentQuantity) => {
    const newQuantity = currentQuantity + 1;
    await handleQuantityChange(detailId, newQuantity);
  };
  
  const handleDecrement = async (detailId, currentQuantity) => {
    if (currentQuantity > 1) {
      const newQuantity = currentQuantity - 1;
      await handleQuantityChange(detailId, newQuantity);
    }
  };

  const calculateTotal = () => {
    return recaporder.reduce((total, item) => {
      const prix = parseFloat(item.prix.replace(/[^\d.-]/g, ''));
      return total + (prix * item.quantity);
    }, 0);
  };

  const total = calculateTotal();
  const quantite = cartQuantity;
  localStorage.setItem('total', total);
  localStorage.setItem('quantite', quantite);

  if (loading) {
    return (
      <>
        <MenuHeader restauId={restau} Table={table} showBackButton={true} cartQuantity={0} />
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f5f5f5', 
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p style={{ marginTop: '15px', color: '#666' }}>Chargement de votre commande...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <MenuHeader restauId={restau} Table={table} showBackButton={true} cartQuantity={0} />
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f5f5f5', 
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="text-center">
            <h3 style={{ color: '#FF6215', marginBottom: '15px' }}>{error}</h3>
            <Link 
              to={`/homemenu?r=${restau}&t=${table}`}
              className="btn btn-primary"
            >
              Retour au menu
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (recaporder.length === 0) {
    return (
      <>
        <MenuHeader restauId={restau} Table={table} showBackButton={true} cartQuantity={0} />
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f5f5f5', 
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="text-center">
            <h3 style={{ color: '#666', marginBottom: '15px' }}>Votre panier est vide</h3>
            <Link 
              to={`/homemenu?r=${restau}&t=${table}`}
              className="btn btn-primary"
            >
              Commencer ma commande
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SessionClient redirectTo={`/homemenu?r=${restau}&t=${table}`} />
      <MenuHeader restauId={restau} Table={table} showBackButton={true} cartQuantity={cartQuantity} />
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#fff', 
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif',
        paddingTop: '80px',
        paddingBottom: '120px'
      }}>
        {/* Order Items */}
        {recaporder.map((item, index) => ( 
          <div key={item.id_details || index} 
            style={{ 
              display: 'flex',
              alignItems: 'flex-start',
              padding: '16px 0',
              borderBottom: index < recaporder.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}>
            {/* Food Image */}
            <div style={{
              width: '95px',
              height: '95px',
              borderRadius: '12px',
              backgroundColor: '#f0f0f0',
              backgroundImage: item.image_url ? `url(${item.image_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              flexShrink: 0,
              marginRight: '12px'
            }} />

            {/* Item Details */}
            <div style={{ flex: 1, paddingRight: '8px' }}>
              <h3 style={{ 
                fontSize: '19px', 
                fontWeight: '500',
                maxWidth: '200px',
                height: '32px',
                color: '#333',
                marginBottom: '4px',
                margin: 0
              }}>
                {item.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#888',
                margin: '2px 0 8px 0',
                lineHeight: '1.3'
              }}>
                {item.underTitle}
              </p>
              {item.commentaire && item.commentaire.trim() !== "" && (
                <p style={{
                  fontSize: '13px',
                  color: '#FF6215',
                  fontStyle: 'italic',
                  margin: '4px 0 8px 0',
                  lineHeight: '1.3',
                  whiteSpace: 'pre-line'
                }}>
                  {item.commentaire}
                </p>
              )}
              <p style={{ 
                fontSize: '20px', 
                fontWeight: '500', 
                color: '#FF6215',
                margin: 0
              }}>
                {(parseFloat(item.prix.replace(/[^\d.-]/g, ''))*item.quantity).toFixed(2)} {devise}
              </p>
            </div>
            
            {/* Quantity Controls */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#E3E3E3',
              borderRadius: '20px',
              padding: '4px',
              gap: '8px',
              flexShrink: 0
            }}>
              <button 
                onClick={() => {
                  if (item.quantity === 1) {
                    handleDelete(item.id_article);
                  } else {
                    handleDecrement(item.id_details, item.quantity);
                  }
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  color: item.quantity === 1 ? '#67748e' : '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {item.quantity === 1 ? (
                  <FaTrash size={12} />
                ) : (
                  <Minus size={12} />
                )}
              </button>
              <span style={{ 
                fontSize: '16px', 
                fontWeight: '500',
                minWidth: '20px',
                textAlign: 'center',
                color: '#333'
              }}>
                {item.quantity}
              </span>
              <button 
                onClick={() => handleIncrement(item.id_details, item.quantity)}
                style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  color: '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                <Plus size={12} />
              </button>
            </div> 
          </div>   
        ))}
        
        {/* Supplements Section */}
        {/* <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '16px'
          }}>
            Suppléments
          </h2> */}
          
          {/* Sample supplement - this would be dynamic based on your data */}
          {/* <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0'
          }}>
            <div style={{ flex: 1 }}>
              <p style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#333',
                margin: 0
              }}>
                Jambon
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#FF6A3D'
              }}>
                1,50 {devise}
              </span>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '20px',
                padding: '4px',
                gap: '8px'
              }}>
                <button style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  color: '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  <Minus size={12} />
                </button>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  minWidth: '20px',
                  textAlign: 'center',
                  color: '#333'
                }}>
                  1
                </span>
                <button style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  color: '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  <Plus size={12} />
                </button>
              </div>
            </div>
          </div>
        </div> */}

        {/* Add More Items Button */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Link 
            to={`/homemenu?r=${restau}&t=${table}`} 
            style={{
              color: '#888',
              fontSize: '16px',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Ajouter des articles
          </Link>
        </div>
      </div>

      {/* Fixed Bottom Section */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        padding: '16px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        zIndex: 1000,
        paddingBottom: '25px',
      }}>
        {/* Payment Button */}
        <Link 
          to={`/specific-instruction?r=${restau}&t=${table}`}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#FFECE3',
            color: '#FF6215',
            border: 'none',
            borderRadius: '12px',
            fontSize: '19px',
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}
        >
          <span>Passer au paiement</span>
          <span>{calculateTotal().toFixed(2)} {devise}</span>
        </Link>

        {/* Totals */}
        {/* <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{ fontSize: '22px', color: '#4B4B4B', fontWeight: '600' }}>Sous-total</span>
          <span style={{ fontSize: '22px', color: '#4B4B4B', fontWeight: '600' }}>
            {(calculateTotal() * 0.857).toFixed(2)} {devise}
          </span>
        </div> */}
        
        {/* <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '20px', color: '#888' }}>TVA</span>
          <span style={{ fontSize: '20px', color: '#888' }}>
            {(calculateTotal() * 0.143).toFixed(2)} {devise}
          </span>
        </div> */}
      </div>
    </>    
  );
};

export default OrderSummary;

